import { Popover, PopoverContent, PopoverTrigger } from '@heroui/react';
import { ChevronDown } from 'lucide-react';
import { FC, MouseEvent, ReactNode, useState } from 'react';

import { useIsMobile } from '@/hooks/useIsMobile';
import { breakpoints } from '@/utils/mediaQueries';

import { CollapsiblePanel, CollapsibleToggle } from './MobileCollapsible.styled';

interface MobileCollapsibleProps {
  children: ReactNode;
  /** Nazwa aktywnej pozycji pokazywana na zwiniętym przycisku. */
  value: string;
  /** Opcjonalny prefiks przed nazwą, np. „Kategorie". */
  label?: string;
  /** Poniżej tej szerokości panel jest zwinięty. Domyślnie tablet i telefon (< 1200 px). */
  breakpoint?: number;
  /** Zamyka popover po kliknięciu przycisku lub linku w środku (wybór pozycji). */
  closeOnSelect?: boolean;
}

/**
 * Boczny panel widoku (nawigacja sekcji, kategorie, warianty) na wąskim ekranie: zamiast listy
 * na początku strony jeden przycisk z aktywną pozycją, który otwiera listę w popoverze.
 * Na szerokim ekranie renderuje dzieci bez zmian.
 */
export const MobileCollapsible: FC<MobileCollapsibleProps> = ({
  children, value, label, breakpoint = breakpoints.lg, closeOnSelect = true,
}) => {
  const isNarrow = useIsMobile(breakpoint);
  const [isOpen, setIsOpen] = useState(false);

  if (!isNarrow) return children;

  const handlePanelClick = (event: MouseEvent<HTMLDivElement>): void => {
    if (!closeOnSelect) return;
    if ((event.target as HTMLElement).closest('button, a')) setIsOpen(false);
  };

  return (
    <Popover placement="bottom-start" offset={6} isOpen={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger>
        <CollapsibleToggle type="button" $open={isOpen}>
          <span>
            {label && `${label}: `}
            <strong>{value}</strong>
          </span>
          <ChevronDown aria-hidden />
        </CollapsibleToggle>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <CollapsiblePanel onClick={handlePanelClick}>{children}</CollapsiblePanel>
      </PopoverContent>
    </Popover>
  );
};
